// ============================================================================
// SYSTEM CONFIGURATION HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { systemConfigService } from 'api/services/systemConfigService';
import type { SystemConfig, UpdateSystemConfigRequest } from 'api/types/systemConfig.types';
import type { ID } from 'api/types';
import { notifyDataChanged, useDataChangeListener } from './useDataSync';

const DATA_KEY = 'system-config';

export const useSystemConfig = () => {
  const [data, setData] = useState<SystemConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await systemConfigService.getAll({ pageSize: 50000 });
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useDataChangeListener(DATA_KEY, fetchConfig);

  return { data, loading, error, refetch: fetchConfig };
};

export const useUpdateSystemConfig = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateConfig = async (id: ID, data: UpdateSystemConfigRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await systemConfigService.update(id, data);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { updateConfig, loading, error };
};
